import { fundraiserExists } from "../fundraisers/fundraiserExists";
import { trimCurrency } from "../utility";
import { FormValidation } from "./FormValidation";

/**
 * Takes the parent validation class and ramps it up to 10.
 * Really it just adds some more methods specific to the fundraiser flow.
 */
export class FundraiserValidation extends FormValidation {
    constructor(form: HTMLFormElement) {
        super(form);
    }

    /**
     * Make sure everything stacks up with the uploaded image
     */
    public checkImage(file: File, uploadInput: HTMLInputElement): boolean {
        // No file was uploaded
        if (!file) {
            uploadInput.setCustomValidity("Please upload a valid image.");

            this.showError(uploadInput, uploadInput.validationMessage);
            return false;
        }

        const { type, size } = file;

        // Make sure the file type is correct
        const accepts = uploadInput.accept.split(", ");

        const correctType = accepts.find((acceptedType) => acceptedType === type);

        // The type is not acceptable
        if (!correctType) {
            uploadInput.setCustomValidity("Please upload a valid image type.");

            this.showError(uploadInput, uploadInput.validationMessage);
            return false;
        }

        // This image is much too large
        if (size > 2e6) {
            uploadInput.setCustomValidity("The image may not be larger than 2MB.");

            this.showError(uploadInput, uploadInput.validationMessage);
            return false;
        }

        uploadInput.setCustomValidity("");
        this.removeError(uploadInput);
        return true;
    }

    /**
     * Check the validation for the form.
     * Send off to proper method as necessary.
     */
    protected async checkValidation(formInput: HTMLInputElement | null): Promise<any> {
        // Make sure the element exists and can be validated. willValidate is needed for IE.
        if (!formInput || !formInput.willValidate) {
            return false;
        }

        if (formInput.id === "Register_Username") {
            await this.checkUsername(formInput);
        }

        if (formInput.id === "Confirm_User_Password") {
            this.confirmPassword(formInput);
        }

        if (formInput.id === "Fundraiser_Name") {
            await this.validateFundraiserName(formInput);
        }

        if (formInput.id === "Donation_Goal") {
            formInput.value = trimCurrency(formInput.value);
        }

        if (formInput.checkValidity()) {
            // All is well, no need to show error
            this.removeError(formInput);

            return true;
        }

        // Get an object of the forms validity state
        const validness = formInput.validity;

        if (validness.valueMissing) {
            this.showError(formInput);
        } else if (validness.typeMismatch) {
            this.showError(formInput, "Please enter a valid email.");
        } else if (validness.patternMismatch) {
            this.determinePattern(formInput);
        } else if (validness.tooShort) {
            this.determineLength(formInput);
        } else {
            this.showError(formInput, formInput.validationMessage);
        }
    }

    /**
     * Make sure that the fundraiser name doesn't already exist.
     * Only applies if the user is logged in.
     */
    private async validateFundraiserName(fundraiserNameInput: HTMLInputElement) {
        const fundraiserName = fundraiserNameInput.value;
        const userFields = document.querySelector(".fundraiser-form__fieldset--account-fields");

        // The user is not logged in, so we can't validate the name
        if (userFields) {
            return;
        }

        // This is the edit form and the fundraiser name matches what we have in the db. No need to validate uniqueness
        if (fundraiserNameInput.dataset.existingName && fundraiserNameInput.dataset.existingName === fundraiserName) {
            fundraiserNameInput.setCustomValidity("");
            return;
        }

        const loader = document.querySelector(".fundraiser-name-loader") as any;

        loader.loading = true;

        const { exists } = await fundraiserExists(fundraiserName);

        // The name already exists for the user, so let's show an error
        if (exists) {
            fundraiserNameInput.setCustomValidity("You already have a fundraiser with this name.");

            loader.loading = false;
            return;
        }

        fundraiserNameInput.setCustomValidity("");
        loader.loading = false;
    }

    private determinePattern(formInput: HTMLInputElement) {
        if (formInput.id === "Donation_Goal") {
            this.showError(formInput, "Please enter a valid dollar amount.");

            return;
        }

        if (formInput.id === "End_Date") {
            this.showError(formInput, "Please enter a valid date.");

            return;
        }
    }
}
